import React, { useState } from 'react';

import {
  useAuth,
  PermissionFilter,
  FEATURE_GATES,
  hasActiveFeatureGate
} from '@sopranodesign/pulse-auth';
import {
  Box,
  Flex,
  Button,
  Paragraph,
  LoadingContainer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';

import { getZendeskSettings } from '../api';
import ZendeskSettingsTabs from './ZendeskSettingsTabs';
import ZendeskKnowledgeBase from './ZendeskKnowledgeBase';
import ZendeskTickets from './ZendeskTickets';
import ZendeskConnectionManager, { ZENDESK_SETTINGS_QUERY_KEY } from './ZendeskConnectionManager';

const Zendesk = () => {
  const auth = useAuth();
  const canEditResource = auth.hasPermission('edit generative ai resource');

  const [section, setSection] = useState('knowledge-base');
  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ZENDESK_SETTINGS_QUERY_KEY,
    queryFn: () => getZendeskSettings()
  });

  const settings = settingsResponse?.data?.settings ?? null;
  const isConnected = settings?.status === 1;

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading Zendesk...</Paragraph>
      </Flex>
    );
  }

  if (!isConnected) {
    return (
      <>
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>
              Connect your Zendesk account to sync support data.
            </Paragraph>

            <PermissionFilter can="edit generative ai resource">
              <Button
                icon="plus"
                variant="primary"
                onClick={() => openModal('connect')}
              >
                Connect Zendesk
              </Button>
            </PermissionFilter>
          </Flex>
        </Box>

        <ZendeskConnectionManager
          activeModal={activeModal}
          modalProps={modalProps}
          closeModal={closeModal}
          openModal={openModal}
        />
      </>
    );
  }

  const renderActionButtons = () => (
    <Flex mt right wrap="true" gap="sm">
      <Button
        icon="key"
        variant="secondary"
        onClick={() => openModal('updateCredential')}
      >
        Update Credential
      </Button>
      <Button
        ml
        icon="trash"
        variant="secondary"
        colour="danger"
        onClick={() => openModal('confirmation', { type: 'disconnect' })}
      >
        Disconnect
      </Button>
    </Flex>
  );

  const sections = hasActiveFeatureGate(FEATURE_GATES.GENERATIVE_AI_ZENDESK_TICKETS)(auth)
    ? [
      { key: 'knowledge-base', label: 'Knowledge Base' },
      { key: 'tickets', label: 'Tickets' }
    ]
    : [{ key: 'knowledge-base', label: 'Knowledge Base' }];

  return (
    <>
      <ZendeskSettingsTabs
        section={section}
        onChange={setSection}
        sections={sections}
        adminSelect={canEditResource}
      />

      {section === 'knowledge-base' && <ZendeskKnowledgeBase settings={settings} />}
      {section === 'tickets' && <ZendeskTickets settings={settings} />}
      {renderActionButtons()}
      <ZendeskConnectionManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        openModal={openModal}
      />
    </>
  );
};

export default Zendesk;